import {
    InsuredGood,
    InsuredGoodCategoryType,
    InsuredGoodDetails,
    InsuredGoodPagination,
    PolicyHolder,
} from "@/shared/domain";
import { sortCategories } from "@/shared/utils";

import { getSpApi, pagedRequestConfig, requestConfig } from "./apiRoot";
import { relatedContractMapper } from "./contractApi";
import { UrlManager } from "./UrlManager";

// Swagger: https://ibpcsrcdev01.portima.net/mybrokerapi/swagger/index.html?urls.primaryName=MyBrokerApi%20V2.0

export const insuredGoodMapper = (source: MyBrokerApi.InsuredGoods): InsuredGood => {
    const relatedContracts = source?.relatedContracts?.length
        ? source.relatedContracts.map((item) => relatedContractMapper(item, source.policyholderId)).unique("id")
        : [];
    return {
        ...source,
        relatedContracts,
        categoryOrder: source.category.id,
        // add your extensions here
    };
};

export const insuredGoodDetailMapper = (source: MyBrokerApi.InsuredGood): InsuredGoodDetails => {
    return {
        ...source,
    };
};

export const policyholderMapper = (source: PolicyHolder): PolicyHolder => {
    return {
        ...source,
        isWaiting: false,
    };
};

/**
 * Gets all insured goods
 */
export type InsuredGoodQueryParameters = Paths.ApiInsuredGood.Get.QueryParameters;

export interface InsuredGoodProfessionalQueryParameters extends Paths.ApiInsuredGoodProfessional.Get.QueryParameters {
    policyholdersIdList?: number[];
}

export const getRawInsuredGoods = async (
    requestParams: InsuredGoodQueryParameters = {},
): Promise<MyBrokerApi.InsuredGoodsPayload> => {
    const axiosRequestConfig = pagedRequestConfig<InsuredGoodQueryParameters>({ params: requestParams });

    const { data } = await getSpApi().get<MyBrokerApi.InsuredGoodsPayload>(
        UrlManager.insuredGoods(),
        axiosRequestConfig,
    );
    return data;
};

export const getInsuredGoodsForProfessional = async (
    requestParams: InsuredGoodProfessionalQueryParameters = {},
    categoryId: string = InsuredGoodCategoryType.ALL,
): Promise<InsuredGoodPagination> => {
    requestParams.categoryCode = categoryId;
    requestParams.policyholderId = requestParams.policyholdersIdList[0];
    delete requestParams.policyholdersIdList;
    const axiosRequestConfig = pagedRequestConfig<InsuredGoodProfessionalQueryParameters>({ params: requestParams });
    const { data } = await getSpApi().get<MyBrokerApi.InsuredGoodsPayload>(
        UrlManager.insuredGoodsProfessional(),
        axiosRequestConfig,
    );
    data.result = data.result.map((item) => insuredGoodMapper(item));
    return data as InsuredGoodPagination;
};

export const getInsuredGoods = async (
    request: InsuredGoodQueryParameters = {},
    categoryId: string = InsuredGoodCategoryType.ALL,
): Promise<InsuredGoodPagination> => {
    request.categoryCode = categoryId;
    const data = await getRawInsuredGoods(request);
    data.result = data.result.map((item) => insuredGoodMapper(item));
    return data as InsuredGoodPagination;
};

/**
 * Gets all insured good categories
 */
export const getInsuredGoodCategories = async (policyholdersIdList = []): Promise<MyBrokerApi.CategoryCounters[]> => {
    const axiosRequestConfig = requestConfig<Paths.ApiInsuredGoodCategoryCounters.Get.QueryParameters>({
        params: {
            policyholdersIdList,
        },
    });
    const { data } = await getSpApi().get<MyBrokerApi.CategoryCounters[]>(
        UrlManager.insuredgoodsCategoryCounters(),
        axiosRequestConfig,
    );
    return sortCategories(data);
};

/**
 * Returns insured good details
 */
export const getRawInsuredGoodDetails = async (
    id: number,
    insuredGoodType: string,
    policyholderId: number,
    ibpAclId: number,
    canRequestGreenCard: boolean,
): Promise<MyBrokerApi.InsuredGood> => {
    const axiosRequestConfig = requestConfig<Paths.ApiInsuredGoodDetails$Id.Get.QueryParameters>({
        params: { insuredGoodType, policyholderId, ibpAclId, canRequestGreenCard },
    });

    const { data } = await getSpApi().get<MyBrokerApi.InsuredGood>(UrlManager.insuredGood(id), axiosRequestConfig);
    return data;
};

export const getInsuredGoodDetails = async (
    id: number,
    insuredGoodType: string,
    policyholderId: number,
    ibpAclId: number,
    canRequestGreenCard: boolean,
): Promise<InsuredGoodDetails> => {
    const data = await getRawInsuredGoodDetails(id, insuredGoodType, policyholderId, ibpAclId, canRequestGreenCard);
    return insuredGoodDetailMapper(data);
};

export const getInsuredGoodsByPolicyHolder = async (
    policyHolderId: number,
    claimDate?: Date,
): Promise<InsuredGood[]> => {
    const axiosRequestConfig = requestConfig<Paths.ApiInsuredGoodPolicyHolder$PolicyHolderId.Get.QueryParameters>({
        params: {},
    });

    if (claimDate) axiosRequestConfig.params.claimDate = claimDate;

    const { data } = await getSpApi().get<InsuredGood[]>(
        UrlManager.insuredGoodsForPolicyholder(policyHolderId),
        axiosRequestConfig,
    );

    return data.map((item) => insuredGoodMapper(item));
};

export const getPolicyholdersWithIG = async (policyholdersIdList: number[] = []): Promise<PolicyHolder[]> => {
    const axiosRequestConfig = requestConfig<Paths.ApiInsuredGoodPolicyHolderlist.Get.QueryParameters>({
        params: {
            policyholdersIdList,
            fileClaim: true,
        },
    });
    const { data } = await getSpApi().get<PolicyHolder[]>(
        UrlManager.policyholderWithInsuredGoods(),
        axiosRequestConfig,
    );
    return data.map((item) => policyholderMapper(item));
};
